import styled, { css } from 'styled-components';
import { Text } from '~/components/ui/Text';
import { ReportState } from '~/shared';

const StyledHeadContainer = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
`;

const StyledHeadInfoContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;

	& > ${Text} {
		font-size: 12px;
		color: #ffffff;
		opacity: 0.5;
		transition: color 0.3s ease;
	}
`;

const StyledTag = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	height: 20px;
	padding: 0 5px;
	border-radius: 5px;
	transition: background-color 0.3s ease;

	${Text} {
		font-weight: 500;
		font-size: 12px;
		transition: color 0.3s ease;
	}
`;

const StyledReportNumber = styled(StyledTag)`
	background: rgba(255, 255, 255, 0.1);

	${Text} {
		color: #ffffff;
	}
`;

const StyledAuthorInfoContainer = styled(StyledTag)<{ $online?: boolean }>`
	gap: 10px;
	background: rgba(255, 255, 255, 0.1);

	${Text} {
		color: #ffffff;
	}

	.dot {
		width: 4px;
		height: 4px;
		border-radius: 4px;

		background: ${({ $online }) => ($online ? '#77DE38' : '#FF3D3D')};
		box-shadow: ${({ $online }) =>
			$online ? '0px 0px 4px rgb(120, 222, 56)' : '0px 0px 4px #FF3D3D'};
	}
`;

const StyledText = styled(Text)`
	margin-top: 17px;
	margin-bottom: 41px;
	font-weight: 500;
	font-size: 14px;
	color: #ffffff;
	transition: color 0.3s ease;
`;

const StyledBottomContainer = styled.div`
	position: absolute;
	bottom: 20px;
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: calc(100% - 40px);
`;

const StyledTagsContainer = styled.div`
	display: flex;
	gap: 10px;
`;

const StyledStatusTag = styled(StyledTag)`
	&.${ReportState.CLOSED} {
		background: rgba(255, 255, 255, 0.1);

		${Text} {
			color: rgba(255, 255, 255, 0.5);
		}
	}

	&.${ReportState.COMPLETED} {
		background: #1f8034;

		${Text} {
			color: #ffffff;
		}
	}

	&.${ReportState.ACTIVE} {
		background: #fabe2c;

		${Text} {
			color: #000000;
		}
	}
`;

const StyledHelpTag = styled(StyledTag)`
	background: #c71e1e;

	${Text} {
		color: #ffffff;
	}
`;

const StyledPendingTag = styled(StyledTag)`
	background: rgba(255, 255, 255, 0.1);

	${Text} {
		color: rgba(255, 255, 255, 0.25);
	}
`;

const StyledAdminTag = styled(StyledTag)<{ $isStaff?: boolean }>`
	background: ${({ $isStaff }) =>
		$isStaff ? 'rgba(255, 255, 255, 0.1)' : '#C71E1E'};

	${Text} {
		color: #ffffff;
	}
`;

const StyledOtherAdminTag = styled(StyledTag)`
	background: rgba(255, 255, 255, 0.1);

	${Text} {
		color: #ffffff;
	}
`;

const StyledDateTimeTag = styled(StyledTag)`
	gap: 10px;
	background: rgba(255, 255, 255, 0.1);

	${Text} {
		color: #ffffff;

		&.date {
			opacity: 0.5;
		}
	}
`;

const StyledContainer = styled.div<{
	$isStaff?: boolean;
	$active?: boolean;
	$invertStatus?: boolean;
}>`
	position: relative;
	min-height: 180px;
	background: ${({ $active }) =>
		$active ? '#1782FF' : 'rgba(255, 255, 255, 0.05)'};
	border: 1px solid rgba(255, 255, 255, 0.1);
	border-radius: 10px;
	padding: 20px;
	transition: background-color 0.3s ease;
	cursor: pointer;

	&:hover {
		background-color: #ffffff;

		${StyledHeadInfoContainer} {
			${Text} {
				color: #000000;
			}

			${StyledReportNumber}, ${StyledAuthorInfoContainer} {
				background-color: rgba(0, 0, 0, 0.1);

				${Text} {
					color: #000000;
				}
			}
		}

		${StyledText} {
			color: #000000;
		}

		${({ $invertStatus }) =>
			$invertStatus &&
			css`
				${StyledStatusTag} {
					background: rgba(0, 0, 0, 0.1);

					${Text} {
						color: rgba(0, 0, 0, 0.5);
					}
				}
			`}

		${({ $isStaff }) =>
			$isStaff &&
			css`
				${StyledAdminTag} {
					background: rgba(0, 0, 0, 0.1);

					${Text} {
						color: #000000;
					}
				}
			`}

    ${StyledOtherAdminTag}, ${StyledDateTimeTag} {
			background: rgba(0, 0, 0, 0.1);

			${Text} {
				color: #000000;
			}
		}
	}
`;

export {
	StyledAdminTag,
	StyledAuthorInfoContainer,
	StyledBottomContainer,
	StyledContainer,
	StyledDateTimeTag,
	StyledHeadContainer,
	StyledHeadInfoContainer,
	StyledHelpTag,
	StyledOtherAdminTag,
	StyledPendingTag,
	StyledReportNumber,
	StyledStatusTag,
	StyledTagsContainer,
	StyledText,
};
